/**
 * PM Workspace Store
 * Zustand store for PM module UI state
 *
 * Handles:
 * - Active canvas/view navigation with URL sync
 * - Selected entities
 * - Filter state persistence
 * - UI preferences
 *
 * URL Synchronization:
 * - When canvas changes, URL is updated via pmNavigation utility
 * - This ensures browser history works correctly
 * - Back/forward buttons work as expected
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { pmNavigate, pmNavigateBack, isNavigationInProgress } from '../utils/pmNavigation'
import type {
  ProjectFilters,
  TaskFilters,
} from '../types'

// ============================================
// TYPES
// ============================================

export type PMCanvas =
  | 'dashboard'
  | 'manager'
  | 'projects'
  | 'project-detail'
  | 'tasks'
  | 'task-detail'
  | 'my-tasks'
  | 'assigned-by-me'
  | 'my-projects'
  | 'sprints'
  | 'sprint-detail'
  | 'backlog'
  | 'milestones'
  | 'gantt'
  | 'timeline'
  | 'calendar'
  | 'my-calendar'
  | 'deadlines'
  | 'resources'
  | 'risks'
  | 'clients'
  | 'client-detail'
  | 'analytics'
  | 'time-tracking'
  | 'workflows'
  | 'chat'
  | 'feed'
  // HR-PM Integration disabled via feature flag - uncomment when ready
  // | 'hrpm-integration'

export type ViewMode = 'list' | 'board' | 'grid' | 'gantt' | 'calendar'

export interface SelectedEntity {
  type: 'project' | 'task' | 'sprint' | 'milestone' | 'client'
  id: string
  data?: unknown
}

interface PMWorkspaceState {
  // Navigation
  activeCanvas: PMCanvas
  previousCanvas: PMCanvas | null

  // Selected entities
  selectedProject: SelectedEntity | null
  selectedTask: SelectedEntity | null
  selectedSprint: SelectedEntity | null
  selectedMilestone: SelectedEntity | null
  selectedClient: SelectedEntity | null

  // Multi-select
  selectedTaskIds: string[]

  // View preferences
  viewMode: ViewMode
  isDetailPanelOpen: boolean
  isSidebarCollapsed: boolean

  // Filters (persisted)
  projectFilters: ProjectFilters
  taskFilters: TaskFilters
  sprintProjectFilter: string | null  // Project ID filter for sprints canvas

  // Modal states
  isProjectModalOpen: boolean
  isTaskModalOpen: boolean
  isSprintModalOpen: boolean

  // Actions - Navigation
  setActiveCanvas: (canvas: PMCanvas) => void
  goBack: () => void

  // Actions - Entity Selection
  selectProject: (id: string, data?: unknown) => void
  selectTask: (id: string, data?: unknown) => void
  selectSprint: (id: string, data?: unknown) => void
  selectMilestone: (id: string, data?: unknown) => void
  selectClient: (id: string, data?: unknown) => void
  setSelectedMilestone: (id: string | null) => void
  clearSelection: () => void

  // Actions - Multi-select
  toggleTaskSelection: (id: string) => void
  selectAllTasks: (ids: string[]) => void
  clearTaskSelection: () => void

  // Actions - View
  setViewMode: (mode: ViewMode) => void
  toggleDetailPanel: () => void
  toggleSidebar: () => void

  // Actions - Filters
  setProjectFilters: (filters: Partial<ProjectFilters>) => void
  setTaskFilters: (filters: Partial<TaskFilters>) => void
  setSprintProjectFilter: (projectId: string | null) => void
  clearFilters: () => void

  // Actions - Modals
  openProjectModal: (projectId?: string) => void
  closeProjectModal: () => void
  openTaskModal: (taskId?: string) => void
  closeTaskModal: () => void
  openSprintModal: (sprintId?: string) => void
  closeSprintModal: () => void

  // Actions - Quick Navigation
  navigateToProject: (id: string, data?: unknown) => void
  navigateToTask: (id: string, data?: unknown) => void
  navigateToSprint: (id: string, data?: unknown) => void
  navigateToMilestone: (id: string, data?: unknown) => void
  navigateToClient: (id: string, data?: unknown) => void

  // Actions - Reset
  reset: () => void
}

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  activeCanvas: 'dashboard' as PMCanvas,
  previousCanvas: null as PMCanvas | null,
  selectedProject: null,
  selectedTask: null,
  selectedSprint: null,
  selectedMilestone: null,
  selectedClient: null,
  selectedTaskIds: [],
  viewMode: 'list' as ViewMode,
  isDetailPanelOpen: false,
  isSidebarCollapsed: false,
  projectFilters: {},
  taskFilters: {},
  sprintProjectFilter: null as string | null,
  isProjectModalOpen: false,
  isTaskModalOpen: false,
  isSprintModalOpen: false,
}

// ============================================
// STORE
// ============================================

export const usePMWorkspaceStore = create<PMWorkspaceState>()(
  persist(
    (set) => ({
      ...initialState,

      // ========================================
      // NAVIGATION
      // ========================================

      setActiveCanvas: (canvas) => {
        // Don't trigger URL navigation if this was called from URL sync (routes.tsx)
        // This prevents infinite loops: click → setActiveCanvas → pmNavigate → URL change → routes.tsx → setActiveCanvas
        const shouldNavigate = !isNavigationInProgress()

        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: canvas,
          // Clear task selection when changing canvas
          selectedTaskIds: [],
        }))

        // Sync URL with canvas state
        if (shouldNavigate) {
          // Get entity ID for detail views from current state
          const state = usePMWorkspaceStore.getState()
          let entityId: string | undefined
          if (canvas === 'task-detail') entityId = state.selectedTask?.id
          else if (canvas === 'project-detail') entityId = state.selectedProject?.id
          else if (canvas === 'sprint-detail') entityId = state.selectedSprint?.id
          else if (canvas === 'client-detail') entityId = state.selectedClient?.id

          pmNavigate(canvas, entityId)
        }
      },

      goBack: () => {
        // Use browser history for proper back navigation
        pmNavigateBack()
        // Also update store state
        set((state) => ({
          activeCanvas: state.previousCanvas || 'dashboard',
          previousCanvas: null,
        }))
      },

      // ========================================
      // ENTITY SELECTION
      // ========================================

      selectProject: (id, data) =>
        set({
          selectedProject: { type: 'project', id, data },
        }),

      selectTask: (id, data) =>
        set({
          selectedTask: { type: 'task', id, data },
        }),

      selectSprint: (id, data) =>
        set({
          selectedSprint: { type: 'sprint', id, data },
        }),

      selectMilestone: (id, data) =>
        set({
          selectedMilestone: { type: 'milestone', id, data },
        }),

      selectClient: (id, data) =>
        set({
          selectedClient: { type: 'client', id, data },
        }),

      setSelectedMilestone: (id) =>
        set({
          selectedMilestone: id ? { type: 'milestone', id } : null,
        }),

      clearSelection: () =>
        set({
          selectedProject: null,
          selectedTask: null,
          selectedSprint: null,
          selectedMilestone: null,
          selectedClient: null,
          selectedTaskIds: [],
        }),

      // ========================================
      // MULTI-SELECT
      // ========================================

      toggleTaskSelection: (id) =>
        set((state) => ({
          selectedTaskIds: state.selectedTaskIds.includes(id)
            ? state.selectedTaskIds.filter((tid) => tid !== id)
            : [...state.selectedTaskIds, id],
        })),

      selectAllTasks: (ids) =>
        set({
          selectedTaskIds: ids,
        }),

      clearTaskSelection: () =>
        set({
          selectedTaskIds: [],
        }),

      // ========================================
      // VIEW PREFERENCES
      // ========================================

      setViewMode: (mode) =>
        set({
          viewMode: mode,
        }),

      toggleDetailPanel: () =>
        set((state) => ({
          isDetailPanelOpen: !state.isDetailPanelOpen,
        })),

      toggleSidebar: () =>
        set((state) => ({
          isSidebarCollapsed: !state.isSidebarCollapsed,
        })),

      // ========================================
      // FILTERS
      // ========================================

      setProjectFilters: (filters) =>
        set((state) => ({
          projectFilters: { ...state.projectFilters, ...filters },
        })),

      setTaskFilters: (filters) =>
        set((state) => ({
          taskFilters: { ...state.taskFilters, ...filters },
        })),

      setSprintProjectFilter: (projectId) =>
        set({ sprintProjectFilter: projectId }),

      clearFilters: () =>
        set({
          projectFilters: {},
          taskFilters: {},
          sprintProjectFilter: null,
        }),

      // ========================================
      // MODALS
      // ========================================

      openProjectModal: (projectId) =>
        set({
          isProjectModalOpen: true,
          selectedProject: projectId
            ? { type: 'project', id: projectId }
            : null,
        }),

      closeProjectModal: () =>
        set({
          isProjectModalOpen: false,
        }),

      openTaskModal: (taskId) =>
        set({
          isTaskModalOpen: true,
          selectedTask: taskId ? { type: 'task', id: taskId } : null,
        }),

      closeTaskModal: () =>
        set({
          isTaskModalOpen: false,
        }),

      openSprintModal: (sprintId) =>
        set({
          isSprintModalOpen: true,
          selectedSprint: sprintId ? { type: 'sprint', id: sprintId } : null,
        }),

      closeSprintModal: () =>
        set({
          isSprintModalOpen: false,
        }),

      // ========================================
      // QUICK NAVIGATION (with URL sync)
      // ========================================

      navigateToProject: (id, data) => {
        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: 'project-detail',
          selectedProject: { type: 'project', id, data },
          isDetailPanelOpen: true,
        }))
        // Sync URL
        pmNavigate('project-detail', id)
      },

      navigateToTask: (id, data) => {
        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: 'task-detail',
          selectedTask: { type: 'task', id, data },
          isDetailPanelOpen: true,
        }))
        // Sync URL
        pmNavigate('task-detail', id)
      },

      navigateToSprint: (id, data) => {
        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: 'sprint-detail',
          selectedSprint: { type: 'sprint', id, data },
        }))
        // Sync URL
        pmNavigate('sprint-detail', id)
      },

      navigateToMilestone: (id, data) => {
        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: 'milestones',
          selectedMilestone: { type: 'milestone', id, data },
        }))
        // Sync URL - milestones doesn't have detail view, just list with selection
        pmNavigate('milestones')
      },

      navigateToClient: (id, data) => {
        set((state) => ({
          previousCanvas: state.activeCanvas,
          activeCanvas: 'client-detail',
          selectedClient: { type: 'client', id, data },
        }))
        // Sync URL
        pmNavigate('client-detail', id)
      },

      // ========================================
      // RESET
      // ========================================

      reset: () => set(initialState),
    }),
    {
      name: 'pm-workspace-storage',
      // Persist navigation state and preferences for seamless refresh
      partialize: (state) => ({
        // Navigation state - persist so refresh maintains current view
        activeCanvas: state.activeCanvas,
        previousCanvas: state.previousCanvas,
        selectedProject: state.selectedProject,
        selectedTask: state.selectedTask,
        selectedSprint: state.selectedSprint,
        selectedMilestone: state.selectedMilestone,
        selectedClient: state.selectedClient,
        // View preferences
        viewMode: state.viewMode,
        isSidebarCollapsed: state.isSidebarCollapsed,
        projectFilters: state.projectFilters,
        taskFilters: state.taskFilters,
      }),
    }
  )
)

// ============================================
// SELECTORS
// ============================================

export const selectActiveCanvas = (state: PMWorkspaceState) => state.activeCanvas
export const selectSelectedProject = (state: PMWorkspaceState) => state.selectedProject
export const selectSelectedTask = (state: PMWorkspaceState) => state.selectedTask
export const selectSelectedMilestone = (state: PMWorkspaceState) => state.selectedMilestone
export const selectSelectedClient = (state: PMWorkspaceState) => state.selectedClient
export const selectViewMode = (state: PMWorkspaceState) => state.viewMode
export const selectProjectFilters = (state: PMWorkspaceState) => state.projectFilters
export const selectTaskFilters = (state: PMWorkspaceState) => state.taskFilters
